/* ============================================================
   m3x1.js — PDP Dispositivo · Oferta General (post-Mudanza)
   Task 2.2: carousel mechanics + accessible tap-to-zoom.

   Vanilla JS, no jQuery. Self-initializing, scoped per
   [data-m3x1-carousel] instance (multiple per page supported).
   Safe under defer and on DOMContentLoaded (idempotent init).
   ============================================================ */
(function () {
  'use strict';

  // Guard de ejecución única (audit 2026-07-04): si este <script> llegara a
  // emitirse dos veces (p.ej. dos instancias de la sección en el editor), la
  // segunda pasada NO debe duplicar los listeners capture a nivel document
  // (interceptor de garantía, gel guard, observers) — se saldría a mitad.
  if (window.__m3x1Loaded) return;
  window.__m3x1Loaded = true;

  // Build marker (survives minification — string literal). Check in console:
  // window.__m3x1Build  -> confirms WHICH version is actually served/running.
  try { window.__m3x1Build = '2026-07-22-general-offer-2'; } catch (e) {}

  var FOCUSABLE =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

  /* ============================================================
     Garantía popup — shared module-scope state (Task 4.2).
     The capture-phase interceptor on Rebuy's checkout button and the
     popup itself live in different functions but coordinate through
     these flags (mirrors how the color popup shares m3x1Continuing
     with initAtc — guard + re-trigger, no divergence, no /cart nav).

       m3x1GarantiaProceed         — set true around our re-click so the
                                     capture handler lets that click pass
                                     through to Vue's checkout().
       m3x1GarantiaOffered         — set true once the popup was offered
                                     and resolved (accept/decline/close)
                                     OR the garantía is already in cart;
                                     subsequent checkout clicks pass
                                     straight through (no nag this session).
       m3x1GarantiaInterceptorBound — guards the single document-level
                                     capture listener install. ============================================================ */
  var m3x1GarantiaProceed = false;
  var m3x1GarantiaOffered = false;
  var m3x1GarantiaInterceptorBound = false;

  /* ============================================================
     Shared cart helpers (module scope) — used by BOTH the ATC
     module (Task 2.8) and the 3-color popup (Continuar). Lifted
     out of initAtc so the popup reuses the EXACT same guarded
     drawer-open + refresh behavior (no divergence, no /cart nav).
     ============================================================ */

  /* Tell the theme + Rebuy + pf-garantia the cart changed so every
     listener re-reads it. rebuy:cart.change is the event Rebuy's
     Smart Cart listens to (confirmed in assets/rebuy-events.js); the
     cart:* trio mirrors what pf-garantia-autoadd dispatches. */
  function dispatchCartRefresh() {
    var events = ['rebuy:cart.change', 'cart:refresh', 'cart:build', 'cart:updated'];
    events.forEach(function (name) {
      try {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
      } catch (e) {
        /* no-op: a missing CustomEvent ctor must never break the flow */
      }
    });
  }

  /* Open the Rebuy Smart Cart drawer. Layered + guarded so a missing
     API never throws AND we never navigate to /cart. Order:
       1) window.Rebuy.SmartCart.{show|open|toggle}()  — public API
       2) click #cart-icon-bubble ONLY if Rebuy is present (Rebuy
          intercepts that <a href="/cart"> click to open the drawer;
          without Rebuy the click would navigate, so we skip it)
     If neither is available we stop (Rebuy's own "open on add" may
     still react to rebuy:cart.change). We NEVER set location = /cart. */
  function openCartDrawer() {
    var rebuy = window.Rebuy;
    var sc = rebuy && rebuy.SmartCart ? rebuy.SmartCart : null;

    if (sc) {
      var methods = ['show', 'open', 'toggle'];
      for (var i = 0; i < methods.length; i++) {
        if (typeof sc[methods[i]] === 'function') {
          try {
            sc[methods[i]]();
            return true;
          } catch (e) {
            /* try the next known method name */
          }
        }
      }
    }

    // Fallback: trigger Rebuy's bound open path via the cart icon, but
    // ONLY when Rebuy exists (otherwise the <a> would navigate to /cart).
    if (rebuy) {
      var icon = document.getElementById('cart-icon-bubble');
      if (icon) {
        try {
          icon.click();
          return true;
        } catch (e) {
          /* swallow — never navigate as a side effect */
        }
      }
    }

    return false;
  }

  function initCarousel(root) {
    if (!root || root.dataset.m3x1Init === '1') return;
    root.dataset.m3x1Init = '1';

    var track = root.querySelector('[data-carousel-track]');
    var slides = Array.prototype.slice.call(
      root.querySelectorAll('[data-carousel-slide]')
    );
    var thumbs = Array.prototype.slice.call(
      root.querySelectorAll('[data-carousel-thumb]')
    );

    if (!track || slides.length === 0) return;

    var activeIndex = 0;
    var isScrollingProgrammatically = false;
    var scrollSettleTimer = null;

    /* ---- Thumbnail active state -------------------------------- */
    function setActive(index) {
      if (index < 0) index = 0;
      if (index > slides.length - 1) index = slides.length - 1;
      if (index === activeIndex) return;
      activeIndex = index;

      thumbs.forEach(function (thumb, i) {
        var on = i === index;
        thumb.classList.toggle('is-active', on);
        if (on) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    /* ---- Scroll a slide into view in the snap track ----------- */
    /* Start-aligned (Mau 2026-07-05): slides use `scroll-snap-align: start`,
       así que el target = offsetLeft del slide (su borde izquierdo pega al
       borde del scrollport). Clamp al rango real de scroll. El CSS usa
       `scroll-snap-stop: always` para que un swipe = UNA imagen; eso también
       bloquearía este salto multi-slide por thumbnail, así que apagamos el
       snap del track (scrollSnapType='none') DURANTE el scroll suave y lo
       restauramos al asentar (re-snap al target, ya alineado, sin tirón). */
    function goToSlide(index) {
      if (index < 0) index = 0;
      if (index > slides.length - 1) index = slides.length - 1;
      var slide = slides[index];
      if (!slide) return;

      var left = slide.offsetLeft;
      var maxLeft = track.scrollWidth - track.clientWidth;
      if (left < 0) left = 0;
      if (left > maxLeft) left = maxLeft;

      isScrollingProgrammatically = true;
      // Deja pasar el salto multi-slide sin que `scroll-snap-stop: always` lo
      // frene un snap point corto.
      track.style.scrollSnapType = 'none';
      // Horizontal scroll only; avoid vertical page jump.
      track.scrollTo({ left: left, behavior: 'smooth' });
      setActive(index);

      window.clearTimeout(scrollSettleTimer);
      scrollSettleTimer = window.setTimeout(function () {
        // Restaura el snap del CSS (x mandatory) — el scroll ya asentó en el target.
        track.style.scrollSnapType = '';
        isScrollingProgrammatically = false;
      }, 400);
    }

    /* ---- Derive active slide from scroll position ------------- */
    function syncActiveFromScroll() {
      if (isScrollingProgrammatically) return;
      var center = track.scrollLeft + track.clientWidth / 2;
      var nearest = 0;
      var nearestDist = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var slideCenter = slides[i].offsetLeft + slides[i].offsetWidth / 2;
        var dist = Math.abs(slideCenter - center);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = i;
        }
      }
      setActive(nearest);
    }

    var rafPending = false;
    track.addEventListener(
      'scroll',
      function () {
        if (rafPending) return;
        rafPending = true;
        window.requestAnimationFrame(function () {
          rafPending = false;
          syncActiveFromScroll();
        });
      },
      { passive: true }
    );

    /* ---- Thumbnails drive the track --------------------------- */
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var idx = parseInt(thumb.getAttribute('data-slide-index'), 10);
        if (!isNaN(idx)) goToSlide(idx);
      });
    });

    /* ---- Keyboard nav on the track ---------------------------- */
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToSlide(activeIndex + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToSlide(activeIndex - 1);
      }
    });

    /* ---- Degradado + asomo en la tira de miniaturas (Mau 2026-07-15) -------
       La tira se desvanece en el borde que tiene mas miniaturas ocultas para
       dejar claro que hay MAS fotos a las que deslizar (reseña, gel, garantia,
       incluye). El fade lo hace mask-image via clase (ver m3x1.css); aqui solo
       elegimos el lado segun scrollLeft. Sin overflow (pocas fotos) no rinde nada. */
    var thumbsEl = root.querySelector('[data-carousel-thumbs]');
    function syncThumbFades() {
      if (!thumbsEl) return;
      var max = thumbsEl.scrollWidth - thumbsEl.clientWidth;
      var canScroll = max > 4;
      var atStart = thumbsEl.scrollLeft <= 2;
      var atEnd = thumbsEl.scrollLeft >= max - 2;
      thumbsEl.classList.toggle('is-fade-right', canScroll && atStart);
      thumbsEl.classList.toggle('is-fade-left', canScroll && atEnd);
      thumbsEl.classList.toggle('is-fade-both', canScroll && !atStart && !atEnd);
    }
    if (thumbsEl) {
      var thumbFadeRaf = false;
      thumbsEl.addEventListener(
        'scroll',
        function () {
          if (thumbFadeRaf) return;
          thumbFadeRaf = true;
          window.requestAnimationFrame(function () {
            thumbFadeRaf = false;
            syncThumbFades();
          });
        },
        { passive: true }
      );
      window.addEventListener('resize', syncThumbFades);
      syncThumbFades();
    }

    initZoom(root, slides);
  }

  /* ============================================================
     Accessible zoom modal
     ============================================================ */
  function initZoom(root, slides) {
    var modal = root.querySelector('[data-zoom-modal]');
    if (!modal) return;

    var dialog = modal.querySelector('[data-zoom-dialog]');
    var img = modal.querySelector('[data-zoom-img]');
    var closeBtn = modal.querySelector('[data-zoom-close]');
    var overlay = modal.querySelector('[data-zoom-overlay]');
    var triggers = Array.prototype.slice.call(
      root.querySelectorAll('[data-zoom-trigger]')
    );

    if (!dialog || !img || triggers.length === 0) return;

    var lastFocused = null;
    var keydownHandler = null;
    // Timer del cierre en transición: si se re-abre dentro de los 200ms del
    // close(), hay que cancelarlo o el timer viejo esconde el modal recién
    // abierto (race del audit 2026-07-04).
    var closeTimer = null;

    function open(trigger) {
      var src = trigger.getAttribute('data-zoom-src');
      var alt = trigger.getAttribute('data-zoom-alt') || '';
      if (!src) return;

      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }

      lastFocused = trigger;
      img.setAttribute('src', src);
      img.setAttribute('alt', alt);

      modal.hidden = false;
      // Force reflow so the CSS transition runs from the open state.
      void modal.offsetWidth;
      modal.classList.add('is-open');
      document.documentElement.classList.add('m3x1-zoom-lock');

      keydownHandler = onKeydown;
      document.addEventListener('keydown', keydownHandler, true);

      // Move focus into the dialog (close button is a safe first stop).
      (closeBtn || dialog).focus();
    }

    function close() {
      if (modal.hidden) return;
      modal.classList.remove('is-open');
      document.documentElement.classList.remove('m3x1-zoom-lock');

      if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler, true);
        keydownHandler = null;
      }

      // Wait out the transition before hiding + clearing the src.
      closeTimer = window.setTimeout(function () {
        closeTimer = null;
        modal.hidden = true;
        img.setAttribute('src', '');
        img.setAttribute('alt', '');
      }, 200);

      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
      lastFocused = null;
    }

    function trapFocus(e) {
      var focusables = Array.prototype.slice
        .call(dialog.querySelectorAll(FOCUSABLE))
        .filter(function (el) {
          return el.offsetParent !== null || el === document.activeElement;
        });
      if (focusables.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !dialog.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        close();
      } else if (e.key === 'Tab') {
        trapFocus(e);
      }
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        open(trigger);
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);
  }

  /* ============================================================
     Boot — find every carousel instance on the page.
     ============================================================ */
  function boot() {
    var roots = document.querySelectorAll('[data-m3x1-carousel]');
    Array.prototype.forEach.call(roots, initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    // Script loaded after DOM is ready (e.g. defer) — boot immediately.
    boot();
  }

  // Re-init when re-rendered by the Theme Editor / Section Rendering API.
  document.addEventListener('shopify:section:load', function (e) {
    var target = e.target || document;
    var roots = target.querySelectorAll
      ? target.querySelectorAll('[data-m3x1-carousel]')
      : [];
    Array.prototype.forEach.call(roots, initCarousel);
  });

  /* ============================================================
     ATC + selector de oferta (Oferta General) — AJAX add-to-cart
     segun el plan elegido + abre el Rebuy Smart Cart.

       "unica"  -> agrega el dispositivo (1x, pago unico).
       "rutina" -> agrega el dispositivo (1x) + el gel automatico
                   (variante + selling_plan de [data-m3x1-gel]).

     Un solo /cart/add.js con los items. Para "rutina" el gel viaja
     en el MISMO add (es la rutina): si el gel falla, el add falla y se
     muestra el error (no dejamos una compra a medias sin el gel).

     INVARIANTE protocolofacial: el form conserva
     data-type="add-to-cart-form". NO disparamos submit (el boton es
     type="button"); replicamos pf-garantia DIRECTO (maybeAddPfGarantia)
     tras el add: agrega la Garantia GRATIS solo si ?ref=protocolofacial
     (sessionStorage pf_garantia) con de-dup via /cart.js, no-bloqueante.
     NUNCA navegamos a /cart; abrimos el drawer de Rebuy.
     ============================================================ */

  // pf-garantia (protocolofacial) directo, sin submit sintetico. No-bloqueante.
  // Modulo scope: lo usa initAtc (antes vivia dentro del popup de color, ya removido).
  function maybeAddPfGarantia() {
    var isPF = false;
    try { isPF = window.sessionStorage.getItem('pf_garantia') === '1'; } catch (e) {}
    if (!isPF) return Promise.resolve();
    var GAR = 46991019933931;
    return window
      .fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var has = (cart.items || []).some(function (i) { return i.variant_id === GAR; });
        if (has) return null; // de-dup: ya esta
        return window.fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: GAR, quantity: 1 }] })
        });
      })
      .catch(function () { return null; }); // la garantia PF nunca rompe la venta
  }

  function initAtc(form) {
    if (!form || form.dataset.m3x1AtcInit === '1') return;
    form.dataset.m3x1AtcInit = '1';

    var btn = form.querySelector('[data-m3x1-atc-btn]');
    var label = form.querySelector('[data-m3x1-atc-label]');
    var errorEl = form.querySelector('[data-m3x1-atc-error]');
    var submitting = false;

    function deviceVariantId() {
      var el = form.querySelector('[data-m3x1-variant-id]');
      var id = el ? parseInt(el.value, 10) : NaN;
      return isNaN(id) ? null : id;
    }

    // Radiogroup del selector ligado a ESTE form por data-plan-form == form id.
    // getAttribute('id') (NO form.id): el <input name="id"> sombrea la prop form.id.
    function planGroup() {
      var formId = form.getAttribute('id');
      if (!formId) return null;
      return document.querySelector('[data-m3x1-plan-group][data-plan-form="' + formId + '"]');
    }
    function selectedPlan() {
      var g = planGroup();
      if (!g) return 'unica'; // sin selector -> compra unica (nunca agrega sub sin pedirlo)
      var checked = g.querySelector('[data-m3x1-plan-radio]:checked');
      return checked ? (checked.getAttribute('data-plan') || checked.value || 'unica') : 'unica';
    }

    // Item del gel de la rutina, leido de [data-m3x1-gel] (server-rendered, no hardcode).
    function gelItem() {
      var el = form.querySelector('[data-m3x1-gel]');
      if (!el) return null;
      var vid = parseInt(el.getAttribute('data-gel-variant'), 10);
      if (isNaN(vid)) return null;
      var item = { id: vid, quantity: 1 };
      var plan = parseInt(el.getAttribute('data-gel-plan'), 10);
      if (!isNaN(plan)) item.selling_plan = plan;
      var note = el.getAttribute('data-gel-note');
      if (note) item.properties = { 'Renovación': note };
      return item;
    }

    function setLoading(on) {
      if (!btn) return;
      if (on) {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-busy', 'true');
        btn.classList.add('is-loading');
        if (label) label.textContent = btn.getAttribute('data-label-loading') || 'Agregando…';
      } else {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-busy');
        btn.classList.remove('is-loading');
        if (label) label.textContent = btn.getAttribute('data-label-default') || label.textContent;
      }
    }
    function showError(msg) {
      if (errorEl) errorEl.textContent = msg || 'No se pudo agregar al carrito. Intenta de nuevo.';
    }
    function clearError() { if (errorEl) errorEl.textContent = ''; }

    function addToCart() {
      if (submitting) return;
      var did = deviceVariantId();
      if (did == null) { showError(); return; }
      submitting = true;
      clearError();
      setLoading(true);

      var items = [{ id: did, quantity: 1 }];
      if (selectedPlan() === 'rutina') {
        var gel = gelItem();
        // La rutina REQUIERE el gel de suscripcion (variante + selling_plan). Si el
        // wiring del editor quedara incompleto, NO degradar a device-only en silencio:
        // el cliente creeria que se suscribio y el descuento del device no dispararia
        // (cobraria distinto a lo mostrado). Abortar con error visible.
        if (!gel || !gel.selling_plan) {
          setLoading(false);
          submitting = false;
          showError('No se pudo configurar la rutina automatica. Elige "Compra unica" o intenta de nuevo.');
          return;
        }
        items.push(gel);
      }

      window
        .fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
        .then(function (res) {
          if (!res.ok) {
            return res.json().catch(function () { return null; }).then(function (body) {
              var msg = body && body.description ? body.description : null;
              throw new Error(msg || 'cart_add_failed');
            });
          }
          return res.json();
        })
        .then(function () { return maybeAddPfGarantia(); })
        .then(function () {
          dispatchCartRefresh();
          openCartDrawer();
          setLoading(false);
          submitting = false;
        })
        .catch(function (err) {
          setLoading(false);
          submitting = false;
          showError(err && err.message && err.message !== 'cart_add_failed' ? err.message : null);
          // Nunca navegar en error.
        });
    }

    if (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        addToCart();
      });
    }
    // Coexistencia pf-garantia: si algo dispara un submit, cancelarlo (no navegar).
    // NUNCA stopPropagation (mataria el listener de captura de pf-garantia).
    form.addEventListener('submit', function (e) { e.preventDefault(); });
  }

  /* ============================================================
     Selector de plan — al cambiar el radio, actualiza el precio +
     la etiqueta de la barra sticky (data-plan-price / data-plan-label
     del radio marcado; cero recomputo). La preseleccion viene
     server-side (radio "rutina" con checked); NO se lee ni escribe
     storage -> visita limpia = Rutina siempre.
     ============================================================ */
  function initPlanSelector(group) {
    if (!group || group.dataset.m3x1PlanInit === '1') return;
    group.dataset.m3x1PlanInit = '1';
    var formId = group.getAttribute('data-plan-form');
    var radios = group.querySelectorAll('[data-m3x1-plan-radio]');
    var sticky = formId ? document.querySelector('[data-m3x1-sticky][data-sticky-form="' + formId + '"]') : null;
    var amountEl = sticky ? sticky.querySelector('[data-m3x1-sticky-amount]') : null;
    var labelEl = sticky ? sticky.querySelector('[data-m3x1-sticky-label]') : null;
    function sync() {
      var checked = group.querySelector('[data-m3x1-plan-radio]:checked');
      if (!checked) return;
      if (amountEl) {
        var price = checked.getAttribute('data-plan-price');
        if (price) amountEl.textContent = price;
      }
      if (labelEl) labelEl.textContent = checked.getAttribute('data-plan-label') || '';
    }
    Array.prototype.forEach.call(radios, function (r) { r.addEventListener('change', sync); });
    sync(); // refleja el default server-side (rutina) + bfcache
  }

  function bootPlanSelector() {
    // Neutraliza el popup viejo "Skindion Days" (<popup-gift-device>): vive en el
    // grupo global "overlays" y se dispara con device-en-carrito. m3x1.js solo corre
    // en este PDP, asi que removerlo aqui lo desactiva SOLO aqui (resto del theme lo
    // conserva). Preserva el comportamiento del PDP live. Idempotente.
    var gift = document.querySelector('popup-gift-device');
    if (gift) { gift.remove(); }
    var groups = document.querySelectorAll('[data-m3x1-plan-group]');
    Array.prototype.forEach.call(groups, initPlanSelector);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootPlanSelector);
  } else {
    bootPlanSelector();
  }
  document.addEventListener('shopify:section:load', function (e) {
    var target = e.target || document;
    var groups = target.querySelectorAll ? target.querySelectorAll('[data-m3x1-plan-group]') : [];
    Array.prototype.forEach.call(groups, initPlanSelector);
  });

  function bootAtc() {
    var forms = document.querySelectorAll('.m3x1__atc-form');
    Array.prototype.forEach.call(forms, initAtc);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAtc);
  } else {
    bootAtc();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var target = e.target || document;
    var forms = target.querySelectorAll
      ? target.querySelectorAll('.m3x1__atc-form')
      : [];
    Array.prototype.forEach.call(forms, initAtc);
  });

  /* ============================================================
     Sticky CTA bar (Task 2.9) — fixed price + button that appears
     once the hero CTA scrolls above the viewport, and hides when
     it scrolls back into view.

     🔴 REUSE (no divergence): the sticky button is type="button",
     lives OUTSIDE the {% form %}, and simply PROXIES a .click() to
     the real ATC submit button [data-m3x1-atc-btn]. That fires the
     form's submit -> initAtc -> 3-color popup (or add-1 fallback)
     -> multi-add + pf-garantia + drawer. Byte-for-byte identical to
     tapping the hero CTA. We add NO submit listener and NEVER call
     stopPropagation here — only a click listener on the sticky btn.

     Visibility uses an IntersectionObserver on the hero ATC form:
     show only when the CTA has scrolled ABOVE the top of the
     viewport (!isIntersecting && boundingClientRect.top < 0) — NOT
     merely because it is below the fold on load. Toggle = remove the
     `hidden` attribute (+ .is-visible for the CSS transition, like
     the modals toggle .is-open). If IntersectionObserver is missing
     we leave the bar hidden (graceful — never crash old browsers).
     ============================================================ */
  function initSticky(root) {
    if (!root || root.dataset.m3x1StickyInit === '1') return;

    // Resolve the hero ATC form + its real submit button (mirrors the popup's
    // data-popup-form lookup). No form or no button -> no-op (never throw).
    var formId = root.getAttribute('data-sticky-form');
    var form = formId ? document.getElementById(formId) : null;
    if (!form) return;
    var realAtcBtn = form.querySelector('[data-m3x1-atc-btn]');
    if (!realAtcBtn) return;

    var stickyBtn = root.querySelector('[data-m3x1-sticky-btn]');
    if (!stickyBtn) return;

    root.dataset.m3x1StickyInit = '1';

    // Timer del hide en transición (race del audit 2026-07-04): un flick
    // rápido de vuelta dentro de los 220ms dejaba la barra oculta el resto
    // del scroll (show() early-return con el timer viejo aún pendiente).
    var hideTimer = null;

    // La burbuja de Chatwoot se sube sobre el sticky vía CSS: al mostrar el sticky
    // ponemos body.m3x1-sticky-on y m3x1.css sube su `bottom` (baja al quitarla).
    function show() {
      if (hideTimer) {
        window.clearTimeout(hideTimer);
        hideTimer = null;
      }
      if (root.classList.contains('is-visible')) return;
      root.hidden = false;
      // Force reflow so the slide-up transition runs from the hidden state.
      void root.offsetWidth;
      root.classList.add('is-visible');
      // Body flag → m3x1.css sube la burbuja de Chatwoot arriba de la barra.
      document.body.classList.add('m3x1-sticky-on');
    }

    function hide() {
      if (root.hidden || hideTimer) return;
      root.classList.remove('is-visible');
      document.body.classList.remove('m3x1-sticky-on');
      // Wait out the transition before hiding (back out of tab order + a11y tree).
      hideTimer = window.setTimeout(function () {
        hideTimer = null;
        root.hidden = true;
      }, 220);
    }

    // Proxy a click to the REAL ATC button — runs the existing flow verbatim.
    // If the real button is disabled (sold out), do nothing.
    stickyBtn.addEventListener('click', function () {
      if (realAtcBtn.disabled || realAtcBtn.hasAttribute('disabled')) return;
      realAtcBtn.click();
    });

    if (typeof window.IntersectionObserver !== 'function') {
      // No IO support: leave the bar hidden (graceful, no crash).
      return;
    }

    // Dos señales (Mau 2026-07-05): PASADO el hero Y ANTES del newsletter del
    // footer. Al entrar el bloque "Suscríbete a nuestro newsletter" al
    // viewport, la barra se esconde para que el resto del footer (métodos de
    // pago, legales) se lea sin que el sticky lo tape.
    var pastHero = false;
    var footerInView = false;
    function syncSticky() {
      if (pastHero && !footerInView) {
        show();
      } else {
        hide();
      }
    }

    var observer = new window.IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          // Past-hero solo cuando el CTA ya scrolleó ARRIBA del viewport.
          // boundingClientRect.top < 0 distingue "ya lo pasé (arriba)" de
          // "aún no llego (below the fold, top positivo)".
          pastHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        }
        syncSticky();
      },
      { threshold: 0 }
    );

    // Observe the hero ATC form: the sticky takes over once it leaves upward.
    observer.observe(form);

    // Observer del footer: target = el bloque del newsletter (mitad del
    // footer); fallback = la sección footer completa si el theme lo renombra.
    var footerTarget =
      document.querySelector('.footer-newsletter-block') ||
      document.getElementById('shopify-section-footer');
    if (footerTarget) {
      var footerObserver = new window.IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            footerInView = entries[i].isIntersecting;
          }
          syncSticky();
        },
        { threshold: 0 }
      );
      footerObserver.observe(footerTarget);
    }
  }

  function bootSticky() {
    var roots = document.querySelectorAll('[data-m3x1-sticky]');
    Array.prototype.forEach.call(roots, initSticky);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootSticky);
  } else {
    bootSticky();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var target = e.target || document;
    var roots = target.querySelectorAll
      ? target.querySelectorAll('[data-m3x1-sticky]')
      : [];
    Array.prototype.forEach.call(roots, initSticky);
  });

  /* ============================================================
     Garantía Prioritaria Premium popup (Task 4.2) — low-ticket
     upsell shown when the shopper taps "Finalizar pedido" inside
     the Rebuy Smart Cart drawer, BEFORE we let them reach checkout.

     🔴 INVARIANT (separate from the ATC submit): this interceptor
     listens on the REBUY CHECKOUT BUTTON (.rebuy-cart__checkout-button),
     NOT on the ATC <form> submit. We NEVER stopPropagation on the ATC
     submit (that would kill pf-garantia). initAtc / initPopup are not
     touched. We NEVER navigate to /cart — "proceed" = re-click Rebuy's
     own button so Vue's checkout() runs.

     The Rebuy checkout button is Vue (binds click via addEventListener,
     no inline attr). A capture-phase listener on document fires BEFORE
     Vue. To BLOCK Vue: preventDefault + stopImmediatePropagation in
     capture (confirmed: stops navigation, no checkout). To PROCEED: set
     the m3x1GarantiaProceed guard, then btn.click() — our capture handler
     sees the guard, lets it pass, Vue's checkout() runs and navigates.

     ── Interceptor: block synchronously, THEN decide async ──────────────
     The capture handler MUST call preventDefault + stopImmediatePropagation
     SYNCHRONOUSLY (before any await) or Vue's bubble-phase checkout() will
     have already fired by the time a promise resolves. So we block first,
     then read /cart.js to decide whether to open the popup or proceed.
     ============================================================ */
  function initGarantiaPopup(root) {
    if (!root || root.dataset.m3x1GarantiaInit === '1') return;
    root.dataset.m3x1GarantiaInit = '1';

    var overlay = root.querySelector('[data-m3x1-garantia-overlay]');
    var dialog = root.querySelector('[data-m3x1-garantia-dialog]');
    var closeBtn = root.querySelector('[data-m3x1-garantia-close]');
    var acceptBtn = root.querySelector('[data-m3x1-garantia-accept]');
    var acceptLabel = root.querySelector('[data-m3x1-garantia-accept-label]');
    var declineBtn = root.querySelector('[data-m3x1-garantia-decline]');
    var errorEl = root.querySelector('[data-m3x1-garantia-error]');

    // Garantía variant id — read from the markup, NEVER hardcoded. This is
    // the SAME variant pf-garantia adds free; the interceptor's "already in
    // cart" check (below) uses it to avoid double-charge / double-offer.
    var variantId = parseInt(root.getAttribute('data-m3x1-garantia-variant'), 10);

    if (!dialog || !acceptBtn) return;

    var lastFocused = null;
    var keydownHandler = null;
    var submitting = false;

    function setLoading(on) {
      if (on) {
        acceptBtn.setAttribute('disabled', 'disabled');
        acceptBtn.setAttribute('aria-busy', 'true');
        acceptBtn.classList.add('is-loading');
        if (acceptLabel) {
          acceptLabel.textContent =
            acceptBtn.getAttribute('data-label-loading') || 'Agregando…';
        }
      } else {
        acceptBtn.removeAttribute('disabled');
        acceptBtn.removeAttribute('aria-busy');
        acceptBtn.classList.remove('is-loading');
        if (acceptLabel) {
          acceptLabel.textContent =
            acceptBtn.getAttribute('data-label-default') || acceptLabel.textContent;
        }
      }
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent =
        msg || 'No se pudo agregar al carrito. Intenta de nuevo.';
    }

    function clearError() {
      if (errorEl) errorEl.textContent = '';
    }

    /* ---- Open / close (mirror of the color popup / zoom modal) ---- */
    // Timer del cierre en transición (race del audit 2026-07-04), espejo del
    // color popup: re-abrir dentro de los 200ms no debe quedar en blanco.
    var closeTimer = null;
    function open(trigger) {
      // Abierto DE VERDAD (sin cierre en transición) -> no re-abrir.
      if (!root.hidden && !closeTimer) return;
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
      lastFocused = trigger || document.activeElement;
      clearError();

      root.hidden = false;
      // Force reflow so the CSS transition runs from the open state.
      void root.offsetWidth;
      root.classList.add('is-open');
      document.documentElement.classList.add('m3x1-zoom-lock');

      keydownHandler = onKeydown;
      document.addEventListener('keydown', keydownHandler, true);

      // Move focus into the dialog (close button is a safe first stop).
      (closeBtn || dialog).focus();
    }

    function close() {
      if (root.hidden || closeTimer) return;
      root.classList.remove('is-open');
      document.documentElement.classList.remove('m3x1-zoom-lock');

      if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler, true);
        keydownHandler = null;
      }

      closeTimer = window.setTimeout(function () {
        closeTimer = null;
        root.hidden = true;
      }, 200);

      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
      lastFocused = null;
    }

    // Close the modal, then continue to Rebuy's checkout. Used by X /
    // overlay / ESC / decline — per Mau, EVERY close path proceeds to
    // checkout; only "Asegurar" changes the outcome (adds the garantía).
    // There is NO "stay in cart" path from this popup.
    function closeAndProceed() {
      close();
      proceedToCheckout();
    }

    function trapFocus(e) {
      var focusables = Array.prototype.slice
        .call(dialog.querySelectorAll(FOCUSABLE))
        .filter(function (el) {
          return el.offsetParent !== null || el === document.activeElement;
        });
      if (focusables.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !dialog.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        // ESC = a close path → mark offered + proceed to checkout.
        m3x1GarantiaOffered = true;
        closeAndProceed();
      } else if (e.key === 'Tab') {
        trapFocus(e);
      }
    }

    /* ---- Asegurar: add garantía variant, flag cart, then proceed ---- */
    function onAccept() {
      if (submitting) return;
      if (isNaN(variantId)) {
        // No resolvable variant — never block the sale; just continue.
        m3x1GarantiaOffered = true;
        closeAndProceed();
        return;
      }
      submitting = true;
      clearError();
      setLoading(true);

      window
        .fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: { _m3x1_garantia: 'auto' }
              }
            ]
          })
        })
        .then(function (res) {
          if (!res.ok) {
            return res
              .json()
              .catch(function () {
                return null;
              })
              .then(function (body) {
                var msg = body && body.description ? body.description : null;
                throw new Error(msg || 'cart_add_failed');
              });
          }
          return res.json();
        })
        .then(function () {
          // Tag the cart so the order tag (Flow) knows the garantía came
          // from THIS popup (vs protocolofacial). Best-effort: a failed
          // attribute write must not abort the checkout the shopper asked
          // for, so we proceed regardless of this call's outcome.
          return window
            .fetch('/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
              },
              body: JSON.stringify({ attributes: { _garantia_via: 'popup' } })
            })
            .catch(function () {
              /* swallow — the garantía is already in the cart */
            });
        })
        .then(function () {
          dispatchCartRefresh();
          setLoading(false);
          submitting = false;
          m3x1GarantiaOffered = true;
          closeAndProceed();
        })
        .catch(function (err) {
          // Add failed: restore the button, show the error, keep the popup
          // open to retry. NEVER proceed / navigate on a failed add.
          setLoading(false);
          submitting = false;
          showError(
            err && err.message && err.message !== 'cart_add_failed'
              ? err.message
              : null
          );
        });
    }

    // Expose open() so the capture interceptor can trigger it by reference.
    root.m3x1GarantiaOpen = open;

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        m3x1GarantiaOffered = true;
        closeAndProceed();
      });
    }
    if (overlay) {
      overlay.addEventListener('click', function () {
        m3x1GarantiaOffered = true;
        closeAndProceed();
      });
    }
    if (declineBtn) {
      declineBtn.addEventListener('click', function () {
        m3x1GarantiaOffered = true;
        closeAndProceed();
      });
    }
    acceptBtn.addEventListener('click', onAccept);
  }

  /* Proceed to checkout = re-click Rebuy's own button so Vue's checkout()
     runs. The m3x1GarantiaProceed guard makes the capture interceptor let
     this synthetic click pass through. We NEVER navigate to /cart. */
  function proceedToCheckout() {
    m3x1GarantiaProceed = true;
    var btn = document.querySelector('.rebuy-cart__checkout-button');
    if (btn) btn.click();
    m3x1GarantiaProceed = false;
  }

  /* Capture-phase interceptor on Rebuy's checkout button. Block FIRST
     (synchronously), then decide async whether to open the popup. */
  function onCheckoutClickCapture(e) {
    var btn =
      e.target && e.target.closest
        ? e.target.closest('.rebuy-cart__checkout-button')
        : null;
    if (!btn) return;

    // Our own re-click (proceedToCheckout) — let it through to Vue.
    if (m3x1GarantiaProceed) return;

    // Already offered + resolved this session — don't nag, let checkout run.
    if (m3x1GarantiaOffered) return;

    // BLOCK Vue's checkout() synchronously, before any await. Do the
    // /cart.js inspection only AFTER the synchronous block.
    e.preventDefault();
    e.stopImmediatePropagation();

    var root = document.querySelector('[data-m3x1-garantia-popup]');
    if (!root) {
      // No popup in the DOM (e.g. garantía variant didn't resolve) — never
      // block the sale; mark offered and proceed straight to checkout.
      m3x1GarantiaOffered = true;
      proceedToCheckout();
      return;
    }

    var variantId = parseInt(root.getAttribute('data-m3x1-garantia-variant'), 10);

    // Abre el popup SOLO si terminó de inicializar; si el root existe pero
    // init falló (markup alterado/incompleto), NUNCA bloquear la venta:
    // marcar ofrecido y pasar directo al checkout. Sin este guard, cada clic
    // de "Finalizar pedido" se cancelaba y tronaba (TypeError doble, sin
    // catch) = checkout bloqueado + script errors (audit 2026-07-04).
    var openPopupOrProceed = function () {
      if (typeof root.m3x1GarantiaOpen === 'function') {
        root.m3x1GarantiaOpen(btn);
      } else {
        m3x1GarantiaOffered = true;
        proceedToCheckout();
      }
    };

    window
      .fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        // If the garantía variant is already a line item (pf-garantia /
        // protocolofacial added it free), don't double-offer — proceed.
        var already = false;
        if (cart && cart.items && !isNaN(variantId)) {
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i] && cart.items[i].variant_id === variantId) {
              already = true;
              break;
            }
          }
        }
        if (already) {
          m3x1GarantiaOffered = true;
          proceedToCheckout();
          return;
        }
        // Fresh offer: open the popup (pass btn for restore-focus on close).
        openPopupOrProceed();
      })
      .catch(function () {
        // /cart.js unreadable — never strand the shopper at the drawer.
        // Open the popup anyway (the accept add still de-dupes server-side
        // via the cart; worst case Mau's "already in cart" never triggers).
        openPopupOrProceed();
      });
  }

  function bootGarantia() {
    var roots = document.querySelectorAll('[data-m3x1-garantia-popup]');
    Array.prototype.forEach.call(roots, initGarantiaPopup);

    // Install the capture-phase interceptor exactly once.
    if (!m3x1GarantiaInterceptorBound) {
      document.addEventListener('click', onCheckoutClickCapture, true);
      m3x1GarantiaInterceptorBound = true;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootGarantia);
  } else {
    bootGarantia();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var target = e.target || document;
    var roots = target.querySelectorAll
      ? target.querySelectorAll('[data-m3x1-garantia-popup]')
      : [];
    Array.prototype.forEach.call(roots, initGarantiaPopup);
    // Re-installs the interceptor if a prior unload removed it.
    if (!m3x1GarantiaInterceptorBound) {
      document.addEventListener('click', onCheckoutClickCapture, true);
      m3x1GarantiaInterceptorBound = true;
    }
  });

  // Editor re-renders: drop the capture listener so it doesn't accumulate
  // across section unload/load cycles (bootGarantia re-installs on load).
  document.addEventListener('shopify:section:unload', function () {
    if (m3x1GarantiaInterceptorBound) {
      document.removeEventListener('click', onCheckoutClickCapture, true);
      m3x1GarantiaInterceptorBound = false;
    }
  });

  /* ============================================================
     Cupón aplicado visible en el drawer (Mau 2026-07-05, "Opción B"
     aprobada por preview) — cuando el carrito trae un DISCOUNT CODE
     aplicado (p.ej. FLASH100 vía /discount/CODE?redirect=...), se
     inyecta una línea arriba del subtotal de Rebuy: ícono de etiqueta
     rosa + "Cupón <CODE> aplicado" (pill) + "−$X" en verde. Confirma
     a quien llegó por el link del flash sale que su descuento YA está.

     - GENÉRICO: lee cart.cart_level_discount_applications de /cart.js
       y muestra solo type === 'discount_code' con monto > 0 (los
       descuentos AUTOMÁTICOS como el BXGY "3x1 Mudanza" viven a nivel
       línea o son type distinto — NO se anuncian como cupón).
     - Sin cupón => la línea no existe (y se remueve si el código se
       cae del carrito).
     - Rebuy re-renderiza su flyout (Vue) y borra el nodo => se
       re-inyecta vía MutationObserver (sin re-fetch; usa el último
       estado) + refresh (con fetch) en los eventos de carrito.
     - SCOPE: donde carga m3x1.js (el PDP 3x1 = el funnel real).
     ============================================================ */
  (function initCartCoupon() {
    var state = null; // { code, amount(cents) } | null
    var TAG_SVG =
      '<svg class="m3x1-cart-coupon__icon" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<path d="M20.59 13.41 11 3.82A2 2 0 0 0 9.59 3.23H5a2 2 0 0 0-2 2v4.59c0 .53.21 1.04.59 1.41l9.58 9.59a2 2 0 0 0 2.83 0l4.59-4.59a2 2 0 0 0 0-2.82z"/>' +
      '<circle cx="7.5" cy="7.5" r="1"/></svg>';

    function inject() {
      var existing = document.querySelector('[data-m3x1-cart-coupon]');
      if (!state) {
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
        return;
      }
      var subtotal = document.querySelector('.rebuy-cart__flyout-subtotal');
      if (!subtotal || !subtotal.parentNode) return;
      // Ya está puesto, actualizado y en su lugar -> no tocar el DOM
      // (evita loop con el MutationObserver).
      if (
        existing &&
        existing.getAttribute('data-code') === state.code &&
        existing.getAttribute('data-amt') === String(state.amount) &&
        existing.parentNode === subtotal.parentNode
      ) {
        return;
      }
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

      var row = document.createElement('div');
      row.className = 'm3x1-cart-coupon';
      row.setAttribute('data-m3x1-cart-coupon', '');
      row.setAttribute('data-code', state.code);
      row.setAttribute('data-amt', String(state.amount));
      row.innerHTML =
        TAG_SVG +
        '<span class="m3x1-cart-coupon__label">Cupón <span class="m3x1-cart-coupon__code"></span> aplicado</span>' +
        '<span class="m3x1-cart-coupon__amt"></span>';
      // textContent (no innerHTML) para el código y el monto: datos externos.
      row.querySelector('.m3x1-cart-coupon__code').textContent = state.code;
      row.querySelector('.m3x1-cart-coupon__amt').textContent =
        '−$' + Math.round(state.amount / 100).toLocaleString('es-MX');
      subtotal.parentNode.insertBefore(row, subtotal);
    }

    function refresh() {
      window
        .fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var apps = (cart && cart.cart_level_discount_applications) || [];
          var codeApp = null;
          for (var i = 0; i < apps.length; i++) {
            if (
              apps[i] &&
              apps[i].type === 'discount_code' &&
              Number(apps[i].total_allocated_amount) > 0
            ) {
              codeApp = apps[i];
              break;
            }
          }
          state = codeApp
            ? {
                code: String(codeApp.title || codeApp.key || 'DESCUENTO'),
                amount: Number(codeApp.total_allocated_amount)
              }
            : null;
          inject();
        })
        .catch(function () {
          /* el aviso del cupón nunca debe romper el carrito */
        });
    }

    var t = null;
    function schedule() {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(refresh, 200);
    }
    ['rebuy:cart.change', 'rebuy:cart.update', 'cart:updated', 'cart:refresh', 'rebuy:smartcart.ready'].forEach(
      function (ev) {
        document.addEventListener(ev, schedule);
      }
    );
    // Rebuy re-pinta el flyout y borra el nodo -> re-inyectar (sin fetch).
    var mt = null;
    function mSchedule() {
      if (mt) window.clearTimeout(mt);
      mt = window.setTimeout(inject, 150);
    }
    try {
      new MutationObserver(mSchedule).observe(document.documentElement, {
        childList: true,
        subtree: true
      });
    } catch (e) {
      /* sin MutationObserver quedan los eventos de carrito */
    }
    schedule(); // estado inicial (p.ej. llegó con el cupón ya en cookie)
  })();

  // ── Fecha de entrega dinamica bajo "Envio gratis" ─────────────────────────
  // Rellena "Llega el 16-20 Julio" en la linea de envio del buybox. La ventana
  // (min/max dias) llega en data-attrs del span (default 2 y 6, = el ejemplo de
  // Mau: hoy 14 -> 16-20). Se calcula en horario de Mexico (America/Mexico_City)
  // para no desfasar a visitantes fuera de MX. El span va `hidden` en el markup y
  // se muestra solo cuando el rango se pinta (si algo falla queda oculto = degrada
  // a "Envio gratis a todo Mexico" a secas). Corre en cada carga (fecha siempre al dia).
  (function initShippingEta() {
    var MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio',
                 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    // "Hoy" segun el calendario de Ciudad de Mexico, como Date a medianoche local
    // (solo se usa para extraer dia/mes, la TZ de construccion es irrelevante).
    function hoyMx() {
      try {
        var p = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/Mexico_City',
          year: 'numeric', month: '2-digit', day: '2-digit'
        }).formatToParts(new Date());
        var y, m, d;
        p.forEach(function (x) {
          if (x.type === 'year') y = parseInt(x.value, 10);
          if (x.type === 'month') m = parseInt(x.value, 10);
          if (x.type === 'day') d = parseInt(x.value, 10);
        });
        if (y && m && d) return new Date(y, m - 1, d);
      } catch (e) {}
      return new Date(); // fallback: hora local del visitante
    }

    function rango(minDias, maxDias) {
      var base = hoyMx();
      var ini = new Date(base); ini.setDate(ini.getDate() + minDias);
      var fin = new Date(base); fin.setDate(fin.getDate() + maxDias);
      var di = ini.getDate(), mi = ini.getMonth();
      var df = fin.getDate(), mf = fin.getMonth();
      if (mi === mf) {
        return di + '–' + df + ' ' + MESES[mf];                     // "16-20 Julio"
      }
      return di + ' ' + MESES[mi] + ' – ' + df + ' ' + MESES[mf];   // "30 Julio - 3 Agosto"
    }

    function apply() {
      var els = document.querySelectorAll('[data-m3x1-eta]');
      if (!els.length) return;
      Array.prototype.forEach.call(els, function (el) {
        var min = parseInt(el.getAttribute('data-eta-min'), 10);
        var max = parseInt(el.getAttribute('data-eta-max'), 10);
        if (isNaN(min)) min = 2;
        if (isNaN(max)) max = 6;
        var out = el.querySelector('[data-m3x1-eta-range]');
        if (!out) return;
        try {
          out.textContent = rango(min, max);
          el.hidden = false;
        } catch (e) { /* deja el span oculto si algo truena */ }
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', apply);
    } else {
      apply();
    }
  })();
})();
